import { useRouter } from "next/router";
import { useMemo } from "react";
import { useTranslation } from "next-i18next";

import useRelatedProducts from "@whoppah/apollo/hooks/useRelatedProducts";
import ProductCard from "@/components/product/ProductCard";
import ProductCardLoading from "@/components/product/ProductCardLoading";
import { SEO } from "components/common";
import Breadcrumb from "components/common/Breadcrumb";
import { Heading } from "@/ui/cva/Heading";
import { useLanguage } from "@/common/hooks/pages.hooks";

const NUMBER_OF_RELATED_PRODUCTS = 48;

const LOADING_PRODUCTS = Array.from(
  { length: NUMBER_OF_RELATED_PRODUCTS },
  (_, i) => i
);

export const SimilarProducts = () => {
  const { t } = useTranslation();
  const router = useRouter();

  const { language } = useLanguage();

  const productSlug =
    typeof router.query.productSlug === "string"
      ? router.query.productSlug
      : "";

  // Should skip fetching if the product slug is not present.
  const skipFetching = !productSlug;

  const { loading, data } = useRelatedProducts({
    variables: {
      slug: productSlug,
      limit: NUMBER_OF_RELATED_PRODUCTS,
      lang: language,
    },
    skip: skipFetching,
  });

  // If we are skipping fetching, we should show loading also.
  const showLoading = skipFetching || loading;

  const products = useMemo(() => data?.relatedProducts ?? [], [data]);

  return (
    <>
      <SEO title={t("similar-products:similar-products-title")} />
      <div className="grid grid-cols-1 gap-4 px-4 py-4 md:px-12 xl:px-24">
        <Breadcrumb
          visibleOnMobile={true}
          items={[{ label: t("similar-products:similar-products-title") }]}
        />
        <Heading as="h1" size="h5" className="-mt-2">
          {t("similar-products:similar-products-title")}
        </Heading>
        <section className="grid grid-cols-2 gap-2 md:grid-cols-4 md:gap-4">
          {showLoading
            ? LOADING_PRODUCTS.map(n => <ProductCardLoading key={n} />)
            : products.map((product, index) => (
                <ProductCard
                  key={product.id}
                  item={product}
                  listName="similar-items"
                  index={index}
                />
              ))}
        </section>
      </div>
    </>
  );
};
